/**
 * ErrorBoundary Component
 * Catches React errors and logs them to the backend
 */
import React, { Component, ReactNode } from "react";
import { AlertTriangle } from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { FeaturedIcon } from "@/components/foundations/featured-icon/featured-icon";
import { isBackendError, logError } from "@/services/error-logging-service";

interface Props {
    children: ReactNode;
    /** Custom fallback UI to show when an error occurs */
    fallback?: ReactNode;
    /** Called when an error is caught (for additional handling) */
    onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error: Error | null;
}

/**
 * React Error Boundary that catches rendering errors and logs them
 * Errors from our backend API are not re-logged (already tracked on server)
 */
export class ErrorBoundary extends Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        // Call custom error handler if provided
        this.props.onError?.(error, errorInfo);

        // Skip logging if the error originated from our backend
        if (isBackendError(error)) {
            console.debug("[ErrorBoundary] Skipping backend error (already logged on server)");
            return;
        }

        // Log the error to the backend
        logError({
            operation: "ReactRenderError",
            errorMessage: error.message,
            stackTrace: error.stack,
            metadata: {
                componentStack: errorInfo.componentStack,
                url: window.location.href,
                userAgent: navigator.userAgent,
            },
            severity: "blocking",
            category: "ReactError",
        });
    }

    render(): ReactNode {
        if (this.state.hasError) {
            // Use custom fallback if provided
            if (this.props.fallback) {
                return this.props.fallback;
            }

            // Default fallback UI
            return (
                <div className="flex min-h-screen flex-col items-center justify-center bg-primary p-8">
                    <div className="flex max-w-md flex-col items-center gap-5 text-center">
                        <FeaturedIcon icon={AlertTriangle} color="error" theme="light" size="xl" />
                        <div className="flex flex-col gap-2">
                            <h1 className="text-display-xs font-semibold text-primary">Something went wrong</h1>
                            <p className="text-md text-tertiary">We've been notified and are working to fix this issue. Please try refreshing the page.</p>
                        </div>
                        <Button color="primary" size="md" onClick={() => window.location.reload()}>
                            Refresh Page
                        </Button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
